import L from 'leaflet';
import 'leaflet.markercluster';

import 'leaflet.markercluster/dist/MarkerCluster.css';

import { DivIcon } from '../Extensions/DivIcon';
import { generateSvgElement, SvgIcon } from '../Extensions/SvgIcon';
import { jsxToHtmlElement } from '../Util';

import type { Feature, Point } from 'geojson';
import { mapOptions } from '../MapSetup';
import type { ClusteredMarkerLayerOptions, Filter, InformationLayer } from './Layers.types';

const defaultOptions: ClusteredMarkerLayerOptions = {
    name: null,
    source: null,
    disablePopup: false,
    persistentInformationLayer: false,
    maxClusterRadius: 40,
    iconCreateFunction: function (this: ClusteredMarkerLayerOptions, cluster: L.MarkerCluster) {
        function getMarkersByPriority(markers) {
            return markers.sort(function (m1, m2) {
                return m2.options.zIndexOffset - m1.options.zIndexOffset;
            });
        }
        let iconElem;

        const childCount = cluster.getChildCount();
        const title = `${childCount} ${this.itemPlural} i området.\nKlicka för att se närmare.`;

        if (this.clusterIconUrl) {
            iconElem = document.createElement('img');
            iconElem.src = this.clusterIconUrl;
        } else {
            const childMarkers = cluster.getAllChildMarkers();
            const topMarker = getMarkersByPriority(childMarkers)[0];
            let iconId;
            if (this.customClusterIconId) {
                iconId = typeof this.customClusterIconId === 'function' ? this.customClusterIconId(childMarkers) : this.customClusterIconId;
            } else {
                iconId = topMarker.options.icon.options.iconId;
            }

            const combinationIcon = topMarker.feature.properties.roadClosed || this.combinationIcon;

            iconElem = generateSvgElement(iconId, combinationIcon);
            iconElem.classList.add('cluster-icon');

            if (combinationIcon) {
                iconElem.classList.add('cluster-icon__larger');
            }
        }
        return new DivIcon({
            title: title,
            html: `
                ${iconElem.outerHTML}
                <div>${childCount}</div>
            `,
            className: 'marker-cluster',
            iconSize: new L.Point(38, 38),
        });
    },
    showCoverageOnHover: false,
};

interface ClusteredMarkerLayer {
    options: ClusteredMarkerLayerOptions;
}

type FeatureConfig = {
    feature: Feature<Point>;
    splitMarkersMinZoom?: number;
    markers?: L.Marker[];
    splitMarkersLatLngs?: L.LatLng[];
    fallbackMarkerLatLng?: L.LatLng;
    filtered?: boolean;
};

type FeatureConfigDict = {
    [featureId: string]: FeatureConfig;
};

const informationLayerPriority = {
    persistent: 0,
    popup: 1,
    hover: 2,
};

class ClusteredMarkerLayer extends L.MarkerClusterGroup {
    _featureConfigs: FeatureConfigDict = {};
    _informationLayers: { [featureId: string]: InformationLayer } = {};
    //Used to render a closed-road layer together with the situation and possible information layer.
    _persistentOverlays: { [featureId: string]: L.Layer } = {};
    _interval: NodeJS.Timer;
    _allFilters: {
        [filterId: string]: Filter;
    };
    _dataLoaded = false;

    constructor(options?: ClusteredMarkerLayerOptions) {
        super({ ...defaultOptions, ...options });

        if (this.options.defaultFilter) {
            this.addFilter('default', this.options.defaultFilter);
        }
    }

    beforeAdd(map: L.Map): this {
        if (!this._dataLoaded) {
            map.fire('beforelayeradd', { layer: this });
        }

        return this;
    }

    onAdd(map: L.Map): this {
        super.onAdd(map);

        if (!this._dataLoaded) {
            this.fire('data:loading');
        }
        this.options.source.subscribe(this.onSourceUpdate);

        map.on('zoomend', this.updateVisibleMarkers, this);

        return this;
    }

    onRemove(map: L.Map): this {
        map.off('zoomend', this.updateVisibleMarkers, this);
        this.options.source.unsubscribe(this.onSourceUpdate);

        Object.values(this._persistentOverlays).forEach((overlay) => overlay.remove());
        this._persistentOverlays = {};

        return super.onRemove(map);
    }

    onSourceUpdate = (features: Feature<Point> | Feature<Point>[]): void => {
        if (!this._dataLoaded) {
            this._dataLoaded = true;
            this.fire('data:loaded');
        }

        if (!Array.isArray(features)) {
            features = [features];
        }

        Object.values(this._featureConfigs).forEach((featureConfig) => {
            if (!features.some((feature) => feature.id === featureConfig.feature.id)) {
                this.removeLayers(featureConfig.markers);
                delete this._featureConfigs[featureConfig.feature.id];
            }
        });

        for (const feature of features) {
            if (!feature.geometry?.coordinates) {
                console.warn(`Invalid feature geometry. Feature Id: ${feature.id}, Layer: ${this.options.name}.`);
                continue;
            }
            const current = this._featureConfigs[feature.id];
            if (current && JSON.stringify(current.feature) === JSON.stringify(feature)) {
                continue;
            }

            if (current?.markers) {
                this.removeLayers(current.markers);
            }

            //Drop any existing overlay so it is rebuilt from the updated feature data.
            this.removePersistentOverlay(feature.id);

            this._featureConfigs[feature.id] = { feature };
        }

        this.updateFeatureMarkers();

        if (this._map) {
            this._map.fire('layeraddcomplete');
        }
    };

    updateFeatureMarkers(): void {
        for (const featureConfig of Object.values(this._featureConfigs)) {
            if (!featureConfig.markers) {
                this.configureFeature(featureConfig);
            }
        }

        this.updateVisibleMarkers();
    }

    configureFeature = (featureConfig: FeatureConfig) => {
        const { feature } = featureConfig;

        const markerPositions = this.options.markerPositions?.(feature);

        const defaultMarkerLatLng = L.GeoJSON.coordsToLatLng(feature.geometry.coordinates as [number, number]);

        if (markerPositions) {
            featureConfig.fallbackMarkerLatLng = defaultMarkerLatLng;

            if (markerPositions.length > 1) {
                featureConfig.splitMarkersMinZoom = this.calculateSplitMarkerMinZoom(markerPositions, this.options.splitMarkersMinDistance ?? 200);
                featureConfig.splitMarkersLatLngs = markerPositions;
            }

            featureConfig.markers = markerPositions.map((latLng) => {
                return this.createMarker(latLng, feature);
            });
        } else {
            featureConfig.markers = [this.createMarker(defaultMarkerLatLng, feature)];
        }

        featureConfig.filtered = !this.filterFeature(feature);
    };

    calculateSplitMarkerMinZoom(markerPositions: L.LatLng[], splitMarkersMinDistance: number): number {
        for (let zoom = mapOptions.minZoom; zoom <= mapOptions.maxZoom; zoom++) {
            const point1 = mapOptions.crs.latLngToPoint(markerPositions[0], zoom);
            const point2 = mapOptions.crs.latLngToPoint(markerPositions[1], zoom);
            const distance = point1.distanceTo(point2);
            if (distance > splitMarkersMinDistance) {
                return zoom;
            }
        }
        return null;
    }

    // Uses this._sourcesFeatures and this._featureMarker to update the markers on the map.
    // Creates markers that are present in this._sourcesFeatures but not yet in this._featureMarker
    // Removes markers that are only present in this.getAllChildMarkers()

    updateVisibleMarkers(): void {
        if (!this._map) {
            return;
        }

        const markersToAdd = [];
        const markersToRemove = [];
        const mapZoom = this._map.getZoom();

        Object.values(this._featureConfigs).forEach((featureConfig) => {
            const { markers, splitMarkersMinZoom, splitMarkersLatLngs, fallbackMarkerLatLng, filtered } = featureConfig;

            if (filtered) {
                markersToRemove.push(...markers);
                return;
            }

            if (markers?.length > 1) {
                if (!splitMarkersMinZoom || mapZoom < splitMarkersMinZoom) {
                    markersToRemove.push(...markers.slice(1));
                    if (!this.hasLayer(markers[0])) {
                        markersToAdd.push(markers[0]);
                    }
                    markers[0].setLatLng(fallbackMarkerLatLng);
                } else {
                    markers.forEach((marker) => {
                        if (!this.hasLayer(marker)) {
                            markersToAdd.push(marker);
                        }
                    });
                    markers[0].setLatLng(splitMarkersLatLngs[0]);
                }
            } else if (markers && !this.hasLayer(markers[0])) {
                markersToAdd.push(markers[0]);
            }
        });

        this.removeLayers(markersToRemove);
        this.addLayers(markersToAdd);

        this.reconcilePersistentOverlays();
    }

    createMarker = (latLng: L.LatLng, feature: Feature<Point>): L.Marker => {
        if (!feature.properties.iconId && !this.options.defaultIconId && !this.options.customIcon) {
            console.warn(`Feature is missing iconId. Feature Id: ${feature.id}, Layer: ${this.options.name}.`);
            return;
        }

        const marker = L.marker(latLng, {
            icon: this.createIcon(feature),
            title: this.options.markerTitle ? this.options.markerTitle(feature.properties) : '',
            zIndexOffset: -feature.properties.sortIndex || 0,
            interactive: !this.options.disablePopup,
        });
        marker.feature = feature;

        marker.on(
            'add',
            () => {
                marker.on('click', this.onMarkerClick, this);
                marker.on('mouseover', this.onMarkerMouseOver, this);
                marker.on('mouseout', this.onMarkerMouseOut, this);
                if (this.options.createPersistentOverlay) {
                    this.showPersistentOverlay(marker);
                }
                if (this.options.persistentInformationLayer) {
                    this.showInformationLayer(marker, informationLayerPriority.persistent);
                }
                if (this.options.onMarkerAdd) {
                    this.options.onMarkerAdd(marker, feature);
                }
            },
            this,
        );
        marker.on(
            'remove',
            () => {
                marker.off('click', this.onMarkerClick, this);
                marker.off('mouseover', this.onMarkerMouseOver, this);
                marker.off('mouseout', this.onMarkerMouseOut, this);
                this.hideInformationLayer(marker.feature.id, informationLayerPriority.persistent);
                this.hidePersistentOverlay(marker.feature);
            },
            this,
        );

        return marker;
    };

    createIcon(feature: Feature): L.Icon<L.BaseIconOptions> {
        if (this.options.customIcon) {
            if (typeof this.options.customIcon === 'function') {
                return this.options.customIcon(feature);
            }
            return this.options.customIcon;
        } else {
            const combinationIcon = feature.properties['roadClosed'] || this.options.combinationIcon;
            return new SvgIcon({
                iconId: feature.properties.iconId || this.options.defaultIconId,
                iconSize: combinationIcon ? [32, 26] : [26, 22],
                popupAnchor: combinationIcon ? [0, -13] : [0, -11],
                combinationIcon: combinationIcon,
                className: 'marker-with-outline',
            });
        }
    }

    bindMarkerPopup(marker: L.Marker): L.Marker {
        const popup = new L.Popup({
            closeButton: false,
            //autoPanPaddingBottomRight: new L.Point(5, 300),
            ...this.options.popupOptions,
            minWidth: 250,
        });
        const popupContent = this.options.popupTemplate(marker.feature.properties, this.options);
        popup.setContent(jsxToHtmlElement(popupContent));

        marker.bindPopup(popup);

        marker.on('popupopen', (e) => {
            this.showInformationLayer(e.target, informationLayerPriority.popup);
        });
        marker.on('popupclose', (e) => {
            this.hideInformationLayer(e.target.feature.id, informationLayerPriority.popup);
        });

        if (this.options.onPopupOpen) {
            marker.on('popupopen', this.options.onPopupOpen);
        }

        popup.on('add', (e) => {
            const target = e.target;
            target
                .getContent()
                .querySelector('.close-button')
                .addEventListener('click', () => target.remove());
        });

        return marker;
    }

    onMarkerMouseOver = (e: L.LeafletMouseEvent): void => {
        if (this.options.onMarkerMouseOver) {
            this.options.onMarkerMouseOver(e);
        }
        const marker = e.target as L.Marker;
        this.showInformationLayer(marker, informationLayerPriority.hover);
    };

    onMarkerMouseOut = (e: L.LeafletMouseEvent): void => {
        if (this.options.onMarkerMouseOut) {
            this.options.onMarkerMouseOut(e);
        }
        const marker = e.target as L.Marker;
        this.hideInformationLayer(marker.feature.id, informationLayerPriority.hover);
    };

    onMarkerClick = (e: L.LeafletMouseEvent): void => {
        if (this.options.onMarkerClick) {
            this.options.onMarkerClick(e);
        }
        if (this.options.disablePopup) {
            return;
        }

        const marker = e.target as L.Marker;
        if (!marker.getPopup() && this.options.popupTemplate) {
            this.bindMarkerPopup(marker).openPopup();
        }
    };

    showInformationLayer = (marker: L.Marker, priority: number): void => {
        if (!this.options.createInformationLayer) {
            return;
        }

        //A persistent overlay (e.g. closed-road) already visualizes the situation extent, skip the information layer
        if (this._persistentOverlays[marker.feature.id]) {
            return;
        }

        let informationLayer = this._informationLayers[marker.feature.id];

        if (informationLayer && this._map.hasLayer(informationLayer) && informationLayer.priority <= priority) {
            return;
        }

        if (!informationLayer) {
            informationLayer = this._informationLayers[marker.feature.id] = this.options.createInformationLayer(
                marker.feature,
                marker,
            ) as InformationLayer;

            if (!informationLayer) {
                return;
            }
        }
        if (informationLayer.priority == null || priority < informationLayer.priority) {
            informationLayer.priority = priority;
        }

        informationLayer.addTo(this._map);
    };

    hideInformationLayer = (id: string | number, priority: number): void => {
        const layer = this._informationLayers[id];

        if (layer && this._map.hasLayer(layer) && priority <= layer.priority) {
            layer.priority = null;
            this._map.removeLayer(layer);
        }
    };

    //Overlay rendered together with the situation, example closed-road layer.
    showPersistentOverlay = (marker: L.Marker): void => {
        if (!this.options.createPersistentOverlay || this._persistentOverlays[marker.feature.id]) {
            return;
        }

        const overlay = this.options.createPersistentOverlay(marker.feature, marker);
        if (!overlay) {
            return;
        }

        this._persistentOverlays[marker.feature.id] = overlay;
        overlay.addTo(this._map);
    };

    hidePersistentOverlay = (feature: Feature): void => {
        // Keep the overlay while another marker for the same feature is still on the map (e.g. the
        // second marker of a both-directions situation, or when clustered).
        const featureConfig = this._featureConfigs[feature.id];
        if (featureConfig?.markers?.some((marker) => this.hasLayer(marker))) {
            return;
        }

        this.removePersistentOverlay(feature.id);
    };

    // Removes overlays whose feature is filtered out or no longer present. Marker 'remove' events are
    // unreliable for clustered markers, so this reconciliation guarantees overlays follow the feature.
    reconcilePersistentOverlays(): void {
        if (!this._map) {
            return;
        }

        Object.keys(this._persistentOverlays).forEach((id) => {
            const featureConfig = this._featureConfigs[id];
            if (!featureConfig || featureConfig.filtered) {
                this.removePersistentOverlay(id);
            }
        });
    }

    private removePersistentOverlay(id: string | number): void {
        const overlay = this._persistentOverlays[id];
        if (overlay) {
            overlay.remove();
            delete this._persistentOverlays[id];
        }
    }

    applyFilters(): void {
        Object.values(this._featureConfigs).forEach((featureConfig) => {
            featureConfig.filtered = !this.filterFeature(featureConfig.feature);
        });

        this.updateVisibleMarkers();
    }

    addFilter(id: string, filter: Filter): this {
        this._allFilters = this._allFilters || {};
        if (this._allFilters[id]) {
            return this;
        }

        this._allFilters[id] = filter;

        this.applyFilters();

        return this;
    }

    removeFilter(id: string): this {
        if (!this.anyFilters()) {
            return this;
        }

        delete this._allFilters[id];

        this.applyFilters();

        return this;
    }

    filterFeature(feature: Feature): boolean {
        return this.filterFeatures([feature]).length > 0;
    }

    filterFeatures(features: Feature[]): Feature[] {
        if (!this.anyFilters()) {
            return features;
        }

        const filtersByOrder = Object.values(this._allFilters).sort((a, b) => a.order - b.order);

        return filtersByOrder.reduce((filteredFeatures, filter) => filter.filterFn(filteredFeatures, features), features);
    }

    anyFilters(): boolean {
        return this._allFilters && Object.keys(this._allFilters).length > 0;
    }
}

export { ClusteredMarkerLayer };
